import * as THREE from "three";

// Note: does not support generateMipmap or anisotropy parameters.
interface RenderTargetParams {
  width;
  height;
  format: THREE.PixelFormat;
  type: THREE.TextureDataType;
  depthBuffer: boolean;
  stencilBuffer: boolean;
  colorSpace: string; // THREE.Texture.colorSpace is typed as string, not THREE.ColorSpace
  samples: 0 | 4;
}

export class RenderTargetPool {
  private readonly freeRenderTargets: THREE.WebGLRenderTarget[] = [];

  private width: number;
  private height: number;

  // tempDrawingBufferSize may be stale, and must be refreshed by calling
  // renderer.getDrawingBufferSize(this.tempDrawingBufferSize) before access.
  private tempDrawingBufferSize = new THREE.Vector2();

  constructor(private renderer: THREE.WebGLRenderer) {
    renderer.getDrawingBufferSize(this.tempDrawingBufferSize);
    this.width = this.tempDrawingBufferSize.x;
    this.height = this.tempDrawingBufferSize.y;
  }

  reserveRenderTargetMatching(
    renderTarget: THREE.WebGLRenderTarget | null,
    paramOverrides?: Partial<RenderTargetParams>,
  ): THREE.WebGLRenderTarget {
    if (renderTarget === null) {
      this.renderer.getDrawingBufferSize(this.tempDrawingBufferSize);
      return this.reserveRenderTarget({
        width: this.tempDrawingBufferSize.x,
        height: this.tempDrawingBufferSize.y,
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        depthBuffer: true,
        stencilBuffer: false,
        colorSpace: THREE.SRGBColorSpace,
        samples: 0,
        ...paramOverrides,
      });
    }
    return this.reserveRenderTarget({
      width: renderTarget.width,
      height: renderTarget.height,
      format: renderTarget.texture.format as THREE.PixelFormat,
      type: renderTarget.texture.type,
      depthBuffer: renderTarget.depthBuffer,
      stencilBuffer: renderTarget.stencilBuffer,
      colorSpace: renderTarget.texture.colorSpace,
      samples: 0, // Not matching render target on purpose, since auxiliary buffers generally should not have MSAA.
      ...paramOverrides,
    });
  }

  /**
   * Each call to reserveRenderTarget should be coupled with a freeRenderTarget call.
   * @param param0
   * @returns
   */
  reserveRenderTarget({
    width,
    height,
    format,
    type,
    depthBuffer,
    stencilBuffer,
    colorSpace,
    samples,
  }: RenderTargetParams): THREE.WebGLRenderTarget {
    // Non-integer pixel ratios could result in non-integer sizes getting passed here.
    width = Math.floor(width);
    height = Math.floor(height);
    for (let i = this.freeRenderTargets.length - 1; i >= 0; --i) {
      if (
        this.freeRenderTargets[i].width === width &&
        this.freeRenderTargets[i].height === height &&
        this.freeRenderTargets[i].texture.format === format &&
        this.freeRenderTargets[i].texture.type === type &&
        this.freeRenderTargets[i].depthBuffer === depthBuffer &&
        this.freeRenderTargets[i].stencilBuffer === stencilBuffer &&
        this.freeRenderTargets[i].texture.colorSpace === colorSpace &&
        this.freeRenderTargets[i].samples === samples
      ) {
        const returnValue = this.freeRenderTargets[i];
        this.freeRenderTargets.splice(i, 1);
        return returnValue;
      }
    }
    const renderTarget = new THREE.WebGLRenderTarget(width, height, {
      format,
      type,
      depthBuffer,
      stencilBuffer,
      colorSpace: colorSpace as THREE.ColorSpace,
      samples,
    });
    renderTarget.texture.userData = {
      widthMultiplier: width / this.width,
      heightMultiplier: height / this.height,
    };
    return renderTarget;
  }

  freeRenderTarget(renderTarget: THREE.WebGLRenderTarget) {
    if (this.freeRenderTargets.length > 5) {
      console.warn("More than 5 free render targets! Disposing the oldest ones.");
      this.freeRenderTargets[0].dispose();
      this.freeRenderTargets[1].dispose();
      this.freeRenderTargets.splice(0, 2);
    }
    const desiredWidth = Math.round(this.width * renderTarget.texture.userData.widthMultiplier);
    if (renderTarget.width !== desiredWidth) {
      renderTarget.width = desiredWidth;
    }
    const desiredHeight = Math.round(this.height * renderTarget.texture.userData.heightMultiplier);
    if (renderTarget.height !== desiredHeight) {
      renderTarget.height = desiredHeight;
    }
    this.freeRenderTargets.push(renderTarget);
  }

  setSize(width: number, height: number) {
    this.width = Math.floor(width);
    this.height = Math.floor(height);
    for (const renderTarget of this.freeRenderTargets) {
      const desiredWidth = Math.round(this.width * renderTarget.texture.userData.widthMultiplier);
      const desiredHeight = Math.round(this.height * renderTarget.texture.userData.heightMultiplier);
      if (renderTarget.width !== desiredWidth || renderTarget.height !== desiredHeight) {
        renderTarget.setSize(desiredWidth, desiredHeight);
      }
    }
  }

  dispose() {
    for (const target of this.freeRenderTargets) {
      target.dispose();
    }
    this.freeRenderTargets.splice(0, this.freeRenderTargets.length);
  }
}
